import React, { useContext, useState } from 'react';
import SvgRobot from '@icons/ai-robot.svg?react';
import { ApiContext } from '@api/api';
import { ExpeditionDetailV4OutDTO, FeaturesEnum, PermissionTypesEnum, RobotSuggestionOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import useNavigation from '@hooks/useNavigation.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import SecuredArea from '@components/security/SecuredArea';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import { formatPrice } from '@utils/priceUtils.ts';
import { suggestionRefusalMessage } from '../i18nKeys';
import { SUGGESTION_AWARD_OFFER, SUGGESTION_AWARD_REFUSED, SUGGESTION_RECOMMEND_CARRIERS, useRobotSuggestions } from './useRobotSuggestions';

interface ComponentProps {
  expedition: ExpeditionDetailV4OutDTO;
  /** The expedition changed under the card — reload the detail around it. */
  onExpeditionChanged: () => void;
  /**
   * Opens the ordinary distribution sidebar with these carriers preselected. The robot recommends
   * *who*, never *how much*, so the price and the deadline stay the user's to enter — which is why
   * this one is not a single click like the award.
   */
  onDistribute: (carrierIds: string[], onDistributed: () => void) => void;
}

/**
 * What the robot suggests doing next with this expedition.
 *
 * Three outcomes that need a person — award this offer, distribute to these carriers, or *nothing
 * was awarded and here is why* — as one list from one endpoint, because a dispatcher reading an
 * expedition does not care about our taxonomy. The third is not an error box: it is advice that
 * happens to be about a refusal.
 *
 * The point of the block is that the button works, and it works by making the **ordinary** call the
 * user could have made by hand, attributed to whoever clicked. Nothing here re-implements award or
 * distribute.
 *
 * Renders nothing at all when there is nothing open, which is the overwhelmingly common case.
 */
const RobotSuggestions: React.FC<ComponentProps> = ({ expedition, onExpeditionChanged, onDistribute }) => {
  const { tr } = useTranslationLgs();
  const nav = useNavigation();
  const { cuAcceptDemand } = useContext(ApiContext);
  const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
  const { suggestions, markAccepted, dismiss } = useRobotSuggestions(expedition?.id);

  /** The suggestion whose button is mid-flight, so only that card's button spins. */
  const [busyId, setBusyId] = useState<string>();

  const resolveSuggestions = useSecuredFlag('robot-suggestion-actions', {
    kind: 'action',
    description: 'Act on or dismiss a robot suggestion',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_SHIPMENT_RW],
  });

  // Editing the rule behind a suggestion is the rule screen's business, not the expedition's, so
  // it carries that screen's own gate rather than the shipment permission the buttons beside it use.
  const editRuleFlag = useSecuredFlag('robot-suggestion-edit-rule', {
    kind: 'action',
    description: 'Open the rule behind a suggestion in the rule editor',
    permissions: [PermissionTypesEnum.PERMISSION_AI_DISPATCHER_RW],
    features: [FeaturesEnum.AI_DISPATCHER],
  });

  if (suggestions.length === 0) return null;

  /**
   * The same call the offers table makes: an award the user performs themselves, so the carrier
   * e-mails, the audit line and the websocket fan-out all come from that endpoint.
   *
   * The price the card showed is sent with it. The endpoint accepts no body at all, but it also
   * refuses a price that no longer matches the offer — and on a one-click button that check is the
   * difference between awarding what the user read and awarding whatever the offer has become.
   */
  const award = (suggestion: RobotSuggestionOutDTO) => {
    setBusyId(suggestion.id);
    cuAcceptDemand(
      expedition.customerId,
      expedition.applicationId,
      suggestion.offer.quoteId,
      suggestion.offer.currency ? { price: { price: suggestion.offer.price, currency: suggestion.offer.currency } } : {},
      () => {
        setBusyId(undefined);
        markAccepted(suggestion.id);
        onExpeditionChanged();
      },
      null,
      {
        onValidationFailed: errors => {
          setBusyId(undefined);
          setBeValidationErrors(errors);
        },
        onError: (_error, showStandardError) => {
          setBusyId(undefined);
          showStandardError();
        },
      },
    );
  };

  const distribute = (suggestion: RobotSuggestionOutDTO) =>
    onDistribute(
      suggestion.carriers.map(carrier => carrier.carrierId),
      () => markAccepted(suggestion.id),
    );

  const editRule = (suggestion: RobotSuggestionOutDTO) => nav.navigate(nav.urlFunctions.aiDispatcherUrlInfo(suggestion.ruleId));

  return (
    <div className="flex flex-col gap-2">
      <BackendValidationComponent />
      {suggestions.map(suggestion => {
        const refused = suggestion.kind === SUGGESTION_AWARD_REFUSED;
        return (
          <div
            key={suggestion.id}
            className={`flex flex-col gap-3 rounded-md border border-solid p-4 md:flex-row md:items-center ${
              refused ? 'border-amber-200 bg-amber-50' : 'border-base-tertiary bg-white'
            }`}
            data-testid={`robotSuggestion-${suggestion.kind}`}
          >
            <SvgRobot className="size-6 shrink-0 text-content-brand" />

            <div className="flex flex-1 flex-col gap-1">
              <span className="text-xs uppercase tracking-wide text-content-tertiary">
                {tr('aidispatcher.suggestion.title', 'Robot suggestion')}
                {' · '}
                {tr('aidispatcher.suggestion.byRule', 'by rule {{rule}}', { rule: suggestion.ruleName })}
              </span>
              <SuggestionBody suggestion={suggestion} />
            </div>

            <SecuredArea id="robot-suggestion-actions" label="Robot suggestion actions" description="Actions on an AI dispatcher suggestion" visible={resolveSuggestions}>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {suggestion.kind === SUGGESTION_AWARD_OFFER && (
                  <RingilButton
                    mode="filled-rectangle"
                    label={tr('aidispatcher.suggestion.award.action', 'Award')}
                    loading={busyId === suggestion.id}
                    disabled={busyId === suggestion.id}
                    onClick={() => award(suggestion)}
                    dataTestId="robotSuggestion-award"
                  />
                )}
                {refused && (
                  <>
                    <RingilButton
                      mode="outlined-rectangle"
                      label={tr('aidispatcher.suggestion.refused.action', 'Award anyway')}
                      loading={busyId === suggestion.id}
                      disabled={busyId === suggestion.id}
                      onClick={() => award(suggestion)}
                      dataTestId="robotSuggestion-awardAnyway"
                    />
                    {/* A deleted rule has nothing to open — the snapshot of its name stays. */}
                    <SecuredArea
                      id="robot-suggestion-edit-rule"
                      label="Edit rule behind a suggestion" description="Edit the AI dispatcher rule behind a suggestion"
                      visible={[
                        editRuleFlag,
                        { type: 'data', label: 'rule still exists', satisfied: !suggestion.ruleDeleted },
                      ]}
                    >
                      <RingilButton
                        mode="text-only"
                        label={tr('aidispatcher.suggestion.editRule', 'Edit rule')}
                        onClick={() => editRule(suggestion)}
                        dataTestId="robotSuggestion-editRule"
                      />
                    </SecuredArea>
                  </>
                )}
                {suggestion.kind === SUGGESTION_RECOMMEND_CARRIERS && (
                  <RingilButton
                    mode="filled-rectangle"
                    label={tr('aidispatcher.suggestion.carriers.action', 'Distribute')}
                    onClick={() => distribute(suggestion)}
                    dataTestId="robotSuggestion-distribute"
                  />
                )}
                <RingilButton
                  mode="text-only"
                  label={tr('aidispatcher.suggestion.dismiss', 'Dismiss')}
                  onClick={() => dismiss(suggestion.id)}
                  dataTestId="robotSuggestion-dismiss"
                />
              </div>
            </SecuredArea>
          </div>
        );
      })}
    </div>
  );
};

/** One suggestion in words: the same card with a different sentence and a different button. */
const SuggestionBody: React.FC<{ suggestion: RobotSuggestionOutDTO }> = ({ suggestion }) => {
  const { tr } = useTranslationLgs();

  switch (suggestion.kind) {
    case SUGGESTION_AWARD_OFFER:
      return (
        <>
          <span className="font-medium">
            {tr('aidispatcher.suggestion.award.headline', 'Suggests awarding {{carrier}} for {{price}}', {
              carrier: suggestion.offer?.carrierName,
              price: formatPrice(suggestion.offer?.price, suggestion.offer?.currency),
            })}
          </span>
          <span className="text-sm text-content-secondary">
            {tr('aidispatcher.suggestion.award.responded', '{{responded}} of {{invited}} carriers responded', {
              responded: suggestion.offer?.respondedCount,
              invited: suggestion.offer?.invitedCount,
            })}
          </span>
        </>
      );

    case SUGGESTION_AWARD_REFUSED:
      return (
        <>
          <span className="font-medium">{tr('aidispatcher.suggestion.refused.headline', 'Nothing was awarded')}</span>
          {/* The ceiling that bit is in the message itself, so overruling it is an informed click. */}
          <span className="text-sm text-content-secondary">{suggestion.refusal && suggestionRefusalMessage(tr, suggestion.refusal)}</span>
          {/* The refusal still carries the offer it declined — which is the thing the user is being
              asked to overrule, so it is named here rather than left to the offers table below. */}
          {suggestion.offer && (
            <span className="text-sm text-content-secondary">
              {tr('aidispatcher.suggestion.refused.bestOffer', 'Best offer: {{carrier}} for {{price}} ({{responded}} of {{invited}} carriers responded)', {
                carrier: suggestion.offer.carrierName,
                price: formatPrice(suggestion.offer.price, suggestion.offer.currency),
                responded: suggestion.offer.respondedCount,
                invited: suggestion.offer.invitedCount,
              })}
            </span>
          )}
        </>
      );

    case SUGGESTION_RECOMMEND_CARRIERS:
      return (
        <>
          <span className="font-medium">
            {tr('aidispatcher.suggestion.carriers.headline', 'Suggests {{count}} carriers for this route', { count: suggestion.carriers?.length ?? 0 })}
          </span>
          {/* One carrier per line and no score: the ranking is the robot's working, and a
              dispatcher deciding whether to send this out reads the names. */}
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-sm text-content-secondary">
            {(suggestion.carriers ?? []).map(carrier => (
              <li key={carrier.carrierId}>{carrier.carrierName}</li>
            ))}
          </ul>
        </>
      );

    default:
      // A kind this release does not know about still deserves to be visible rather than silently
      // dropped — the backend extends this list without a frontend release.
      return <span className="font-medium">{tr('aidispatcher.suggestion.title', 'Robot suggestion')}</span>;
  }
};

export default RobotSuggestions;
